import { useEffect, useState } from "react";
import API from "../services/api";
import "../styles/reports.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function ManagerReports() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    dealer_id: "",
    status: "",
    from: "",
    to: ""
  });

  const format = (num) => Number(num || 0).toLocaleString();

  // ================= LOAD REPORT =================
  const loadReport = async () => {
    try {
      setLoading(true);

      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v)
      );

      const query = new URLSearchParams(cleanFilters).toString();

      const res = await API.get(
        `/manager/mid-report${query ? `?${query}` : ""}`
      );

      setData(res.data.data || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.log("Report Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= LOAD DEALERS =================
  const loadDealers = async () => {
    try {
      const res = await API.get("/manager/dealers");
      setDealers(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadDealers();
    loadReport();
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // ================= EXPORT EXCEL =================
  const exportExcel = () => {
    if (!data.length) {
      alert("No data to export");
      return;
    }

    const exportData = data.map((c) => ({
      Salesman: c.salesman_name,
      Dealer: c.dealer_name,
      Customer: c.customer_name,
      Mobile: c.customer_mobile,
      City: c.city,
      Category: c.category_name,
      QR: c.qr_value,
      Amount: c.claim_amount,
      Date: new Date(c.created_at).toLocaleDateString(),
      Status: c.manager_status
    }));

    const totalAmount = data.reduce(
      (sum, c) => sum + Number(c.claim_amount || 0),
      0
    );

    exportData.push({
      Salesman: "",
      Dealer: "",
      Customer: "",
      Mobile: "",
      City: "",
      QR: "",
      Amount: totalAmount,
      Date: "",
      Status: "TOTAL"
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const file = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });

    saveAs(file, `Manager_Report_${Date.now()}.xlsx`);
  };

  return (
    <div className="reports-container">

      {/* ================= HEADER ================= */}
      <div className="reports-header">
        <h2>Manager Reports</h2>

        <button className="export-btn" onClick={exportExcel}>
          Export Excel
        </button>
      </div>

      {/* ================= FILTER ================= */}
      <div className="top-bar">

        <select name="dealer_id" className="input" onChange={handleChange}>
          <option value="">All Dealers</option>
          {dealers.map((d) => (
            <option key={d.dealer_id} value={d.dealer_id}>
              {d.dealer_name}
            </option>
          ))}
        </select>

        <select name="status" className="input" onChange={handleChange}>
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>

        <input type="date" name="from" className="input" onChange={handleChange} />
        <input type="date" name="to" className="input" onChange={handleChange} />

        <button className="btn" onClick={loadReport}>
          Apply
        </button>

      </div>

      {/* ================= SUMMARY ================= */}
      <div className="cards">

        <div className="card-mini">
          <p>Total Claims</p>
          <h3>{summary.totalClaims || 0}</h3>
        </div>

        <div className="card-mini">
          <p>Total Amount</p>
          <h3>Rs {format(summary.totalAmount)}</h3>
        </div>

        <div className="card-mini">
          <p>Approved Amount</p>
          <h3>Rs {format(summary.approvedAmount)}</h3>
        </div>

        <div className="card-mini">
          <p>Pending Amount</p>
          <h3>Rs {format(summary.pendingAmount)}</h3>
        </div>

        <div className="card-mini">
          <p>Rejected Amount</p>
          <h3>Rs {format(summary.rejectedAmount)}</h3>
        </div>

      </div>

      {loading && <p style={{ textAlign: "center" }}>Loading...</p>}

      {/* ================= TABLE ================= */}
      <div className="table-card">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Salesman</th>
              <th>Dealer</th>
              <th>Customer</th>
              <th>Mobile</th>
              <th>City</th>
              <th>Product Name</th>
              <th>Model</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  No Data Found
                </td>
              </tr>
            ) : (
              data.map((c) => (
                <tr key={c.id}>
                  <td>{c.salesman_name}</td>
                  <td>{c.dealer_name}</td>
                  <td>{c.customer_name}</td>
                  <td>{c.customer_mobile}</td>
                  <td>{c.city}</td>
                  <td>{c.category_name}</td> 
                  <td>{c.qr_value}</td>
                  <td>Rs {format(c.claim_amount)}</td>
                  <td>{new Date(c.created_at).toLocaleDateString()}</td>
                  <td>
                    <span className={`badge ${c.manager_status}`}>
                      {c.manager_status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}